/**
 * Is this CampaignProduct an ABANDONED STUB?
 *
 * SCOPE NOTE (2026-09-12): this predicate is no longer the whole discard
 * rule. It is now the SECOND of two conditions -- `discardStubOnLeave.js`
 * gates first on the productEditor store's sticky authoring-intent flag and
 * only then consults this function, and both must agree before a row is
 * deleted. The reason is in that module's header: field presence could not
 * distinguish a SKU the system auto-filled from one a user picked, and in
 * production it was always the former, so this check alone never fired.
 * Nothing below changes -- keeping it as a second opinion means a
 * disagreement between the two always resolves to "keep the row".
 *
 * Jake, 2026-08-27: "When I create a new product on the Products page in the
 * store editor and don't touch anything in that product and go back, it
 * shouldn't be persisted. I only want the product to actually be created if the
 * Supplier and SKU are filled in."
 *
 * WHY THIS IS A "DISCARD ON EXIT" AND NOT A "DEFER THE CREATE"
 * ------------------------------------------------------------
 * The Create Product CTA (StoreProducts/index.vue `addProduct`) POSTs to
 * /campaign_product/create_stub and routes into the editor with a real
 * campaign_products id. The row therefore already exists by the time the user
 * can abandon it. Deferring the create instead would mean the editor has no id
 * to autosave against, no id for image uploads, decorations, or the store-
 * defaults apply hook that fires on create -- a much larger redesign. So the
 * fix is symmetric to the create: if the user leaves without giving the row a
 * reason to exist, discard it.
 *
 * WHAT "SUPPLIER AND SKU ARE FILLED IN" RESOLVES TO IN DATA TERMS
 * ---------------------------------------------------------------
 * It resolves to ONE field: `product_item_number` (the SKU).
 *
 * The Supplier control in SupplierSkuPanel.vue is `const supplier =
 * ref('sanmar')` -- a local-only widget whose non-SanMar options are disabled
 * "Coming soon". It is never written to the store and never persisted. The
 * columns that DO record a supplier (`supplier_id` / `supplier_name`) are
 * populated SERVER-side, derived from the SKU by the
 * `_resolve_sanmar_sku_on_save` before_insert/before_update listener in
 * api/src/models/campaign_product.py: a style the SanMar EPDD prices IS a
 * SanMar style, and the listener resolves the vendor row from it.
 *
 * So supplier is a FUNCTION of the SKU, not an independent user choice.
 * "Supplier and SKU are filled in" cannot mean two independent checks, because
 * there is no second thing for the user to fill in. Requiring a non-null
 * supplier_id here would ALSO be wrong in a way that bites: the SKU autosaves
 * from the client, and the supplier linkage only appears in the payload the
 * editor holds after a subsequent server round trip -- so a product whose SKU
 * the user just picked would read as "abandoned" for a window of time. Keying
 * on the SKU is both the user's actual act and the causally upstream one.
 *
 * WHAT "DIDN'T TOUCH ANYTHING" RESOLVES TO
 * -----------------------------------------
 * Jake described the untouched case but stated the rule in terms of fields. A
 * user who typed a NAME but picked no SKU is not "untouched", and silently
 * deleting their typing would be the one unrecoverable mistake this feature
 * can make. So a stub is abandoned only when BOTH hold:
 *
 *   1. no SKU, AND
 *   2. no other authored content -- name still the seeded placeholder, and
 *      every other user-authorable field empty.
 *
 * A product with typed content but no SKU is INCOMPLETE, not abandoned; it is
 * left alone and no warning is shown. That is deliberately narrower than "warn
 * whenever the SKU is missing": it means a distributor part-way through
 * configuring a product can leave and come back, which is a legitimate
 * workflow, and it keeps the warning's wording honest (nothing the user typed
 * is ever the thing being discarded).
 *
 * SAFE DIRECTION
 * --------------
 * Every uncertainty resolves to FALSE (do not discard). An unloaded product, a
 * payload with no recognizable attributes, or an unexpected shape all read as
 * "not abandoned" -- the cost of leaking one stub row is a stray row a user can
 * delete; the cost of a false positive is destroying real work.
 */

// The names the stub-create endpoint seeds, plus the casing variant
// SupplierSkuPanel's auto-fill already treats as a placeholder. Kept in sync
// with AUTO_FILL_NAME_PLACEHOLDERS there and with the `name` default in
// api/src/endpoints/campaign_product.py::create_stub_campaign_product.
const SEEDED_NAME_PLACEHOLDERS = new Set(["New product", "New Product"]);

// Fields a user can author on a brand-new product BEFORE picking a SKU. Any
// non-empty value here means real work exists and the row must be kept.
// Deliberately excludes anything the system writes on its own (supplier_cost,
// blank_cost_basis_snapshot, default_sale_price, images, ...) -- those are
// derived from a SKU that by definition is not present in the abandoned case,
// and counting them would make the guard stop firing for reasons the user
// never caused.
//
// THE MEMBERSHIP TEST IS "CAN THE SERVER WRITE THIS ON AN UNTOUCHED STUB?"
// ------------------------------------------------------------------------
// Not "is this field editable in the UI" -- every field below is both, and the
// distinction is what this list gets wrong when it gets it wrong. A field the
// create path can populate before the user has done anything is not evidence
// of authorship, and including one silently disables the discard for every
// store that happens to have the corresponding setting configured.
//
// `default_margin` was removed on 2026-09-12 for exactly that reason, and it
// is the worked example worth keeping: `POST /campaign_product/create_stub`
// best-effort calls `apply_store_defaults_to_new_product`, which resolves the
// store's (or distributor's) margin and writes it straight onto the new row --
// `api/src/endpoints/store_defaults.py`, `UPDATE campaign_products SET
// default_margin = :m`. The result was a discard that worked in stores with no
// configured margin and silently did nothing in stores that had one: store 44
// on sandbox 8083 (margin 0.4500) accumulated 19 orphaned rows, ALL carrying a
// non-null margin, while store 51 (margin NULL, same distributor) had zero.
// A margin of 0 was the worst case -- `isEmptyValue` counts 0 as non-empty on
// purpose, so a zero-margin store could never discard anything at all.
//
// Dropping it costs no protection. A user who really sets a margin does it
// through PricingPanel's blur handlers, which call
// `productEditor.updateField('default_margin', ...)`, and `updateField` marks
// the sticky authoring-intent flag unconditionally -- so a genuine margin edit
// is caught by the guard's PRIMARY gate, which observes the user rather than
// the row. What was removed is only the reading that could not tell a person
// from the store-defaults hook.
//
// The rest of the list was audited at the same time and every member survives:
// no server path writes any of them on a fresh stub. The four JSONB columns
// (`selected_sizes`, `selected_colors`, `selected_color_skus`,
// `non_standard_upcharges`) DO get SQLAlchemy column defaults at insert, but
// those are `[]` / `{}`, which `isEmptyValue` already treats as empty -- they
// are safe as long as that stays true, so do not "fix" isEmptyValue to count
// empty containers.
const USER_AUTHORABLE_FIELDS = [
    "short_description",
    "long_description",
    "description",
    "category",
    "selected_sizes",
    "selected_colors",
    "selected_color_skus",
    "non_standard_upcharges",
    "min_quantity",
    "max_quantity",
    "quantity_per_shopper_override",
];

// Keys whose mere PRESENCE tells us this payload is a real CampaignProduct
// attribute bag rather than an empty object we were handed by mistake.
const RECOGNITION_KEYS = ["product_name", "product_item_number"];

function isBlankString(value) {
    return value == null || (typeof value === "string" && value.trim() === "");
}

// Empty for our purposes: null/undefined, blank string, empty array, empty
// object. A `0` or `false` is NOT empty -- a user who set a margin of 0 or
// cleared a flag has authored something.
function isEmptyValue(value) {
    if (value == null) return true;
    if (typeof value === "string") return value.trim() === "";
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === "object") return Object.keys(value).length === 0;
    return false;
}

/**
 * True when `attributes` describes a product the user created and then left
 * without giving it a SKU or authoring anything.
 *
 * @param {object|null|undefined} attributes  The EFFECTIVE CampaignProduct
 *   attribute bag (dirty-over-server), as produced by the productEditor
 *   store. Callers must pass effective values so an unsaved SKU pick counts.
 * @returns {boolean} true only when it is safe to discard the row.
 */
export function isAbandonedProductStub(attributes) {
    if (attributes == null || typeof attributes !== "object") return false;

    // Unrecognizable payload (e.g. `{}` before the editor's load resolves):
    // we do not know what this row holds, so we never call it abandoned.
    const looksLikeCampaignProduct = RECOGNITION_KEYS.some((key) =>
        Object.prototype.hasOwnProperty.call(attributes, key),
    );
    if (!looksLikeCampaignProduct) return false;

    // 1. A SKU means the product is real. Nothing else matters.
    if (!isBlankString(attributes.product_item_number)) return false;

    // Defensive: a row carrying a supplier linkage without a SKU is data
    // someone deliberately created (or a legacy row), not a stub this flow
    // produced. Leave it alone.
    if (attributes.supplier_id != null) return false;
    if (!isBlankString(attributes.supplier_name)) return false;

    // 2. A renamed product is authored work, SKU or not.
    const name = attributes.product_name;
    if (name != null && typeof name === "string") {
        const trimmed = name.trim();
        if (trimmed !== "" && !SEEDED_NAME_PLACEHOLDERS.has(trimmed)) {
            return false;
        }
    }

    // 3. Any other authored content keeps the row.
    for (const field of USER_AUTHORABLE_FIELDS) {
        if (!isEmptyValue(attributes[field])) return false;
    }

    return true;
}

/**
 * Does this payload carry USER-AUTHORED content?
 *
 * The second-opinion check for the intent-gated discard (2026-09-12). It is
 * `isAbandonedProductStub` MINUS the SKU and supplier conditions, and that
 * subtraction is the entire point:
 *
 *   - `product_item_number`, `supplier_id`, `supplier_name` are written by
 *     something other than the user. Every orphaned row on sandbox 8083
 *     carried all three while being provably unconfigured, so treating them as
 *     evidence of authorship is what made the original guard silently inert.
 *     The sticky intent flag now covers the case they were standing in for --
 *     a user who really did pick a SKU sets the flag by doing so.
 *
 *   - The NAME and the user-authorable fields are different: nothing writes
 *     those except a person typing. They stay, so a reload that loses the
 *     in-memory flag still cannot delete a product someone visibly worked on.
 *     That is what makes this a real second opinion rather than a rubber stamp.
 *
 * @param {object|null|undefined} attributes  EFFECTIVE attribute bag.
 * @returns {boolean} true when a human has authored something here.
 */
export function hasAuthoredContent(attributes) {
    if (attributes == null || typeof attributes !== "object") return true;

    const name = attributes.product_name;
    if (name != null && typeof name === "string") {
        const trimmed = name.trim();
        if (trimmed !== "" && !SEEDED_NAME_PLACEHOLDERS.has(trimmed)) {
            return true;
        }
    }

    for (const field of USER_AUTHORABLE_FIELDS) {
        if (!isEmptyValue(attributes[field])) return true;
    }

    return false;
}

/**
 * Is this a product with NO SKU that may be offered for deletion? (card #233)
 *
 * Jake, 2026-08-27: "I only want the product to actually be created if the
 * Supplier and SKU are filled in. If not, then warn when navigating out that
 * the product will be removed/deleted with the option to cancel navigation."
 * The leave guard asks for every product this returns true for, whatever the
 * user did to it; the user confirms before anything is deleted.
 *
 * SAFE DIRECTION: a payload that does not carry the SKU column (not loaded,
 * unrecognizable) is not SKU-less, and an ORDERED product is never offered
 * for deletion -- order lines reference it.
 *
 * @param {object|null|undefined} attributes  EFFECTIVE attribute bag.
 * @returns {boolean}
 */
export function hasNoSku(attributes) {
    if (attributes == null || typeof attributes !== "object") return false;
    if (!Object.prototype.hasOwnProperty.call(attributes, "product_item_number")) {
        return false;
    }
    if (attributes.product_item_number === undefined) return false;
    if (attributes.is_ordered === true) return false;
    return isBlankString(attributes.product_item_number);
}

/**
 * Every field `isAbandonedProductStub` can key on. Callers that assemble an
 * EFFECTIVE attribute bag use this to make sure a dirty edit is included even
 * when the server payload never carried that column.
 */
export const ABANDONMENT_RELEVANT_FIELDS = Object.freeze([
    "product_name",
    "product_item_number",
    "supplier_id",
    "supplier_name",
    "is_ordered",
    ...USER_AUTHORABLE_FIELDS,
]);

export const __testing = {
    SEEDED_NAME_PLACEHOLDERS,
    USER_AUTHORABLE_FIELDS,
};
